import React from 'react';
import shortid from 'shortid';
import createDefinitions from './createDefinitions';
import {
  UnsupportedSchemaTypeError,
  SchemaTypeMismatchError,
  DataToSchemaTypeMismatchError,
} from './errors';
import { HIDDEN } from './formats';
import buildSchemaMismatchReport from './helpers/buildSchemaMismatchReport';
import createComponent from './helpers/createComponent';
import findData from './helpers/findData';
import findDefinition from './helpers/findDefinition';
import upsertDefinition from './helpers/upsertDefinition';
import {
  TypeString,
  TypeObject,
  TypeArray,
  TypeInteger,
  TypeNumber,
} from './overrides';
// Basic styles are loaded by default and need to be enabled by the user:
// <Renderer className="ellington-basic">
import './themes/index.scss';
import {
  OBJECT,
  STRING,
  ARRAY,
  INTEGER,
  NUMBER,
  BOOLEAN,
  NULL,
  COMPONENT,
} from './types';

export const parseSchema = (displaySchema, data) => {
  // scan and interpolate any: title, description fields.
  const newSchema = {
    ...displaySchema,
  };

  const interpolate = function (text, data = {}) {
    const names = Object.keys(data);
    const vals = Object.values(data);
    return new Function(...names, `return \`${text}\`;`)(...vals);
  };

  let newTitle;

  try {
    newTitle = interpolate(displaySchema.title, data);
  } catch {
    //  this will only show if interpolation fails.
    newTitle = 'N/A';
  }
  newSchema.title = newTitle;

  return newSchema;
};

const Renderer = props => {
  const {
    children: Children = [],
    displaySchema: schema,
    data: payloadData,
    shouldDisplayBlankFields = false,
    enableCopyField = false,
    customCopyFieldComponent = null,
    components = '',
    render = () => {},
    accessToken = '',
    ...rest
  } = props;

  const displaySchema = parseSchema(schema, payloadData);

  const {
    className,
    onError = () => {},
    root = true,
    isChildRoot = false,
    definitions = createDefinitions(),
    path = [],
  } = rest;

  // Gather any custom components provided by the user.
  React.Children.forEach(Children, child => {
    const { type } = child;
    const { displayName: typeName } = type;
    if (typeName === TypeString.displayName) {
      upsertDefinition(definitions[STRING].customs, child);
    } else if (typeName === TypeObject.displayName) {
      upsertDefinition(definitions[OBJECT].customs, child);
    } else if (typeName === TypeArray.displayName) {
      upsertDefinition(definitions[ARRAY].customs, child);
    } else if (typeName === TypeNumber.displayName) {
      upsertDefinition(definitions[NUMBER].customs, child);
    } else if (typeName === TypeInteger.displayName) {
      upsertDefinition(definitions[INTEGER].customs, child);
    } else if (typeName === BOOLEAN) {
      definitions[BOOLEAN].customs.push(child);
    } else if (typeName === NULL) {
      definitions[NULL].customs.push(child);
    } else if (typeName === COMPONENT) {
      definitions[COMPONENT].customs.push(child);
    } else {
      throw new UnsupportedSchemaTypeError(type.name);
    }
  });

  /**
   * Recursively walks the JSON Schema specification payload, delegating
   * rendering along the way and returning a single rendered component.
   * @param {Object} schema - The JSON Schema
   * @param {*} path - The schema path to start recursing on. By default,
   * empty indicated the root.
   */
  const walkSchema = (schema, path = []) => {
    const { propertiesOrder, properties } = schema;
    let sortKeys = propertiesOrder || [];

    if (!sortKeys.length) {
      if (schema instanceof Array) {
        sortKeys = schema;
      } else {
        sortKeys = [schema];
      }
    }

    return sortKeys
      .map(key => {
        // NOTE if the key is a string, it's a propertiesOrder key. Otherwise,
        // the key is likely to be a subset of the entire schema.

        const isPropertiesOrderKey = typeof key === 'string';

        let property = key;
        if (isPropertiesOrderKey) {
          // An attempt to fail gracefully if the given key is not found within
          // the listed properties. If that's the case, null is returned so React
          // knows to ignore it as a rendered component.
          if (!properties[key]) {
            return null;
          }
          property = properties[key];
        } else if (JSON.stringify(property) === '{}') {
          return null;
        }

        const { type } = property;

        const configPath = [...path, isPropertiesOrderKey ? key : null].filter(
          v => v !== null,
        );
        if (!configPath.length) {
          return null;
        }

        const config = {
          definitions,
          displaySchema,
          schema: property,
          data: payloadData,
          path: configPath,
          onError,
          shouldDisplayBlankFields,
          components,
          render,
          ownProps: rest,
          accessToken,
          enableCopyField,
          customCopyFieldComponent,
        };

        if (property.format && property.format === HIDDEN) {
          return null;
        }

        if (type === OBJECT) {
          const Component = createComponent(OBJECT, config);
          const result = walkSchema(property, config.path);
          return <Component key={shortid.generate()}>{result}</Component>;
        } else if (type === ARRAY) {
          // According to spec, items can be an object for single type elements
          // or an array for multi-type elements. This implementation assumes that
          // items are defined as an object literal and that all items in the
          // array are of the same type.
          const { items } = property;
          const Component = createComponent(ARRAY, config);
          const raw = findData(config.data, config.path);

          if (!raw) {
            // Data is optional, so if not found, return an empty component.
            return null;
          }

          if (!(raw instanceof Array)) {
            const { extra, fingerprint, tags } = buildSchemaMismatchReport({
              type,
              property,
              path: config.path,
              displaySchema: config.displaySchema,
              ownProps: config.ownProps,
              data: raw,
              errorType: 'DataToSchemaTypeMismatch',
              fingerprintPrefix: 'ellington-data-schema-type-mismatch',
            });
            onError(
              new DataToSchemaTypeMismatchError(type),
              extra,
              fingerprint,
              tags,
            );
            return null;
          }

          return (
            <Component key={shortid.generate()}>
              {raw.map((item, idx) => {
                return walkSchema(items, [...config.path, idx]);
              })}
            </Component>
          );
        } else if (type === STRING) {
          const Component = createComponent(STRING, config);
          return <Component key={shortid.generate()} />;
        } else if (type === INTEGER) {
          const Component = createComponent(INTEGER, config);
          return <Component key={shortid.generate()} />;
        } else if (type === NUMBER) {
          const Component = createComponent(NUMBER, config);
          return <Component key={shortid.generate()} />;
        } else if (type === BOOLEAN) {
          const Component = createComponent(BOOLEAN, config);
          return <Component key={shortid.generate()} />;
        } else if (type === NULL) {
          const Component = createComponent(NULL, config);
          return <Component key={shortid.generate()} />;
        } else if (type === COMPONENT) {
          const Component = render({
            config,
            schema: config.schema,
            data: config.data,
            ownProps: config.ownProps,
          });
          return <Component key={shortid.generate()} />;
        }

        // If the schema type isn't supported throw an error.
        const { extra, fingerprint, tags } = buildSchemaMismatchReport({
          type,
          property,
          path: config.path,
          displaySchema: config.displaySchema,
          ownProps: config.ownProps,
          data: findData(config.data, config.path),
        });
        onError(new SchemaTypeMismatchError(type), extra, fingerprint, tags);
      })
      .filter(child => {
        //filter out null
        if (child) {
          return child;
        }
      });
  };

  // It's assumed that the root property is always a JSON Schema type object.
  const { Component: RootComponent } = findDefinition(
    definitions,
    schema,
    OBJECT,
    { root, isChildRoot },
  );

  const children = walkSchema(schema);

  if (isChildRoot) {
    return (
      <>
        <RootComponent schema={schema} path={path} {...rest}>
          {children}
        </RootComponent>
      </>
    );
  }

  if (className) {
    return (
      <div className={className}>
        <RootComponent schema={schema} {...rest}>
          {children}
        </RootComponent>
      </div>
    );
  }

  return (
    <RootComponent schema={schema} {...rest}>
      {children}
    </RootComponent>
  );
};

export default Renderer;
